<template>
  <div class="row">
    <div class="col-xs-4" style="position: sticky; top: 100px; z-index: 1;">
      <h2 class="m-b-20 section-title">{{ $t('Additional services') }}</h2>
      <div class="pickup-toggle-box">
        <span class="d-inline-block m-r-10">{{ $t('delivery_to_drop_point') }}</span>
        <Toggle id="pickupPointDelivery" v-model="isDroppoint" :key="`update-${update}`"/>
      </div>
      <div>
        <ToggleList
          :shippingDate="shippingDate"
          :list="servicesList"
          :servicesAllowed="servicesCompiledList"
          :selectedServices="selectedServices"
          :additionalFieldData="additionalFieldData"
          @servicesUpdate="servicesUpdate"
          @extraFieldAdd="extraFieldAdd" />
      </div>
    </div>
    <div class="col-xs-8">
      <h2 class="m-b-20 section-title">{{ $t('Available options') }}</h2>
      <!-- Filter options -->
      <div class="row m-b-30" v-if="get_user.should_show_freight_prices">
        <div class="col-xs-6">
          <div
            :class="activeFilter === 'cheapest' ? 'active brand' : ''"
            class="button-bar shadowed"
            @click="activeFilter = 'cheapest'">
            <Icon
              class="m-r-20"
              name="coins"
              :circle="true"
              size="24"/>
            <div class="text-box">
              <span class="main-text">{{ $t('Cheapest') }}</span>
            </div>
          </div>
        </div>
        <div class="col-xs-6">
          <div
            :class="activeFilter === 'fastest' ? 'active brand' : ''"
            class="button-bar shadowed"
            @click="activeFilter = 'fastest'">
            <Icon
              class="m-r-20"
              name="deliveryFast"
              :circle="true"
              size="24"/>
            <div class="text-box">
              <span class="main-text">{{ $t('Fastest') }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- Skeleton blanko -->
      <div v-if="productsList == null || (productsList.length == 0 && reloadingProducts)">
        <div class="toggle-card-box card p-40 relative"
          v-for="index in 3"
          :key="`productSkeletonEmty-${index}`">
          <div class="card-skeleton-layout">
            <Skeleton
              height="55px"
              width="62px"
              class="m-r-20"
            />
            <div class="text-box-skeleton">
              <Skeleton
                height="15px"
                width="150px"
              />
              <Skeleton
                height="15px"
                width="120px"
              />
            </div>
          </div>
          <div class="skeleton-price">
            <Skeleton
              height="25px"
              width="70px"
            />
          </div>
        </div>
      </div>
      <message
        v-if="productsList != null && productsList.length == 0 && !reloadingProducts"
        name="warning"
        :data="data={data: $t('no_shipping_options'), type:'validation'}" />
      <!-- Skeleton -->
      <div v-if="reloadingProducts">
        <div class="toggle-card-box card p-40 relative"
          v-for="(product, index) in productsCompiledList"
          :key="`productSkeleton-${product.shipping_product_id}-${index}`">
          <div class="card-skeleton-layout">
            <Skeleton
              height="55px"
              width="62px"
              class="m-r-20"
            />
            <div class="text-box-skeleton">
              <Skeleton
                height="15px"
                width="150px"
              />
              <Skeleton
                height="15px"
                width="120px"
              />
            </div>
          </div>
          <div class="skeleton-price">
            <Skeleton
              height="25px"
              width="70px"
            />
          </div>
        </div>
      </div>
      <!-- Products -->
      <div v-if="!reloadingProducts">
        <ToggleCard
          v-for="(product, index) in productsCompiledList"
          :key="`product-${product.shipping_product_id}-${index}`"
          :product="product"
          :showPrices="showPrices"
          :pickupDate="pickupDate"
          :editShipmentDroppointNo="editShipmentDroppointNo"
          :editPickupDriverNote="pickupDriverNote"
          :editPickupTimeUntil="pickupTimeUntil"
          :editPickupTimeFrom="pickupTimeFrom"
          :selectedServices="selectedServices"
          :customsData="customsData"
          :enabledProducts="enabledProductsByServices"
          :enabledInsuranceCategory="isInsuranceCategoryEnabled"
          :isOpened="(selectedProduct != null) && (selectedProduct.shipping_product_id == product.shipping_product_id)"
          @updateInsuranceCategory="updateInsuranceCategoryAndMakeCall"
          @triggerShipment="bookShipment"
          @selected="selectProduct" />
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import _ from 'lodash';
import ToggleList from '@/components/global/toggleList/index.vue';
import ToggleCard from '@/components/global/toggleCard/index.vue';
import Toggle from '@/components/elements/inputs/toggle.vue';
import { Skeleton } from 'vue-loading-skeleton';

import mixin from '@/mixins/mixins.js';

export default {
  name: 'AdditionalServices',
  mixins: [mixin],
  components: {
    Toggle,
    ToggleList,
    ToggleCard,
    Skeleton,
  },
  props: {
    editShipmentDroppointNo: {
      type: [String, Number],
      default: '',
    },
    showColliContent: {
      type: Array,
      default: [],
    },
    shipperAsSenderID: {
      type: [String, Number],
      default: '',
    },
    pickupDate: {
      type: [String, Date],
      default: '',
    },
    shippingDate: {
      type: [String, Date],
      default: '',
    },
    dataForProducts: {
      type: Object,
      default() {
        return null;
      },
    },
    observableObject: {
      type: Object,
      default() {
        return null;
      },
    },
    companyFromListener: {
      type: String,
      default: '',
    },
    companyToListener: {
      type: String,
      default: '',
    },
    shipToPrivateResident: {
      type: Boolean,
      default: false,
    },
    fromReferenceListener: {
      type: Object,
      default: () => {
        return {
          ref: '',
          required: false,
        };
      },
    },
    toReferenceListener: {
      type: Object,
      default: () => {
        return {
          ref: '',
          required: false,
        };
      },
    },
    incotermServiceIds: {
      type: Array,
      default() {
        return [];
      },
    },
    apiFeeIds: {
      type: Array,
      default() {
        return [];
      },
    },
    apiActiveServices: {
      type: Object,
      default() {
        return null;
      },
    },
    selectedProductId: {
      type: [Number, String],
      default: '',
    },
    additionalServicesFieldsData: {
      type: Object,
      default() {
        return null;
      },
    },
    pickupTimeFrom: {
      type: [String],
      default: '',
    },
    customsData: {
      type: [Object],
      required: false
    },
    pickupTimeUntil: {
      type: [String],
      default: '',
    },
    pickupDriverNote: {
      type: [String],
      default: '',
    },
  },
  data() {
    return{
      insuranceCategory: null,
      showPrices: false,
      showProductType: '1', //show all products
      reloadingProducts: false,
      update: 0,
      activeFilter: 'cheapest',
      servicesList: null,
      servicesAllowed: [],
      enabledServicesByProduct: [],
      // cachedEnabledServices: [],
      productsList: null,
      productsAllowed: [],
      enabledProductsByServices: [],
      // FILTER DATA
      byPrice: '',
      // DATA
      selectedProduct: null, // cannot be used in bookShipment method, cause its not up to date, cause it is updated on select product event and some actions are done after that event
      selectedServices: [],
      // Additional field data
      additionalFieldData: {},
      isDroppoint: false,
      externalApiLoaded: false,
    };
  },
  computed: {
    ...mapGetters([
      'get_user',
    ]),
    isInsuranceCategoryEnabled() {
      if(this.servicesList == null) return false;
      let result = false;
      this.servicesList.forEach((serviceGroup) => {
        if(parseInt(serviceGroup.id) === 9) {
          Object.keys(serviceGroup.services).forEach((key) => {
            if(this.selectedServices.indexOf(serviceGroup.services[key].id) > -1){
              result = true;
            }
          })
        }
      })
      return result;
    },
    productsCompiledList(){
      // IF NO SERVICES CHOOSEN
      if(this.productsList === null) return null;
      // IF SERVICES CHOOSEN

      //If all products should be shown without prices, then sort them by delivery time
      if (this.showProductType == 1 && !this.showPrices) {
        this.activeFilter = 'fastest';
      }

      //If only one cheapest product should be shown, then active filter should be set to cheapest
      if (this.showProductType == 2) {
        this.activeFilter = 'cheapest';
      }

      //If only one fastest product should be shown, then active filter should be set to fastest
      if (this.showProductType == 3) {
        this.activeFilter = 'fastest';
      }

      // FILTER LIST
      let productsListFiltered = this.productsList;

      let productsValidPrice = [];
      let productsValidNoPrice = [];
      let productsServiceErrorPrice = [];
      let productsServiceErrorNoPrice = [];
      let productsWithErrorPrice = [];
      let productsWithErrorNoPrice = [];

      this.productsList.forEach((product) => {
        if(this.enabledProductsByServices.indexOf(product.shipping_product_id) == -1 && this.enabledProductsByServices.length > 0){
          if(product.prices_data.total_price == 0){
            productsServiceErrorNoPrice.push(product);
          } else {
            productsServiceErrorPrice.push(product);
          }
        } else {
          if(product.prices_data.total_price == 0){
            if('error' in product.prices_data) {
              productsWithErrorNoPrice.push(product);
            } else if(product.api_errors.length > 0) {
              productsWithErrorNoPrice.push(product);
            } else {
              productsValidNoPrice.push(product)
            }
          } else {
            if('error' in product.prices_data) {
              productsWithErrorPrice.push(product);
            } else if(product.api_errors.length > 0) {
              productsWithErrorPrice.push(product);
            } else {
              productsValidPrice.push(product)
            }
          }
        }
      })

      if(this.activeFilter !== '') {
        // Return by filter
        if(this.activeFilter == 'fastest'){
          productsListFiltered = [
            ...this.sortByTime([
              ...this.sortByPrice(productsValidPrice),
              ...this.sortByPrice(productsValidNoPrice)
            ]),
            ...this.sortByTime([
              ...this.sortByPrice(productsServiceErrorPrice),
              ...this.sortByPrice(productsServiceErrorNoPrice)
            ]),
            ...this.sortByTime([
              ...this.sortByPrice(productsWithErrorPrice),
              ...this.sortByPrice(productsWithErrorNoPrice)
            ]),
          ];
        }
        if(this.activeFilter == 'cheapest'){
          productsListFiltered = [
            ...this.sortByPrice(productsValidPrice),
            ...this.sortByPrice(productsValidNoPrice),
            ...this.sortByPrice(productsServiceErrorPrice),
            ...this.sortByPrice(productsWithErrorPrice),
            ...this.sortByPrice(productsServiceErrorNoPrice),
            ...this.sortByPrice(productsWithErrorNoPrice),
          ];
        }
      }

      //Show one cheapest or fastest product only
      if (['3', '2'].includes(this.showProductType.toString())) {
        return [productsListFiltered[0]];
      }

      // Return without filter
      return productsListFiltered;
    },
    servicesCompiledList() {
      if(this.selectedProduct == null) {
        // IF PRODUCT NOT SELECTED && SERVICE SELECTED
        if(this.selectedServices.length > 0){
          this.enabledServicesByProduct = [];
          if(this.productsList != null){
            this.productsList.forEach((product) => {
              if(this.enabledProductsByServices.indexOf(product.shipping_product_id) > -1){
                product.allowed_services.forEach((id) => {
                  if(this.enabledServicesByProduct.indexOf(parseInt(id)) < 0) {
                    this.enabledServicesByProduct.push(parseInt(id));
                  }
                });
              }
            });
            return this.servicesAllowed;
          }
          return this.enabledServicesByProduct;
        } else {
          return this.servicesAllowed;
        }

      } else {
        // IF PRODUCT SELECTED
        return this.enabledServicesByProduct;
      }
    },
  },
  created() {
    if(this.dataForProducts === null) return;
  },
  mounted() {
    console.log(this.selectedProduct)
  },
  methods: {
    findSelectedServiceIdByCategory(category) {
      let returnServiceId = null;
      //Dynamically find insurance selected service ID
      this.servicesList.forEach((serviceGroup) => {
        Object.keys(serviceGroup.services).forEach((key) => {
          const serviceId = serviceGroup.services[key].id;
          if (serviceGroup.id == category && this.selectedServices.includes(serviceId)) {
            returnServiceId = serviceId.toString();
          }
        });
      });

      return returnServiceId
    },
    sortByPrice(arr) {
      return arr.sort((a, b) =>
        (a.prices_data.total_price > b.prices_data.total_price) ||
        (a.prices_data.total_price == b.prices_data.total_price) ? 1 : -1);
    },
    sortByTime(arr) {
      return arr.sort((a, b) =>
          ((a.estimated_time.compare > b.estimated_time.compare) ||
          (a.estimated_time.compare == b.estimated_time.compare) ? 1 : -1));
    },
    updateInsuranceCategory(val) {
      const insuranceServiceId = this.findSelectedServiceIdByCategory(9); //Insurance category

      if (!insuranceServiceId) {
        console.log('Insurance service Id not found');
        return;
      }

      this.insuranceCategory = val;
      this.additionalFieldData[insuranceServiceId] = {...this.additionalFieldData[insuranceServiceId], category: val};
    },
    updateInsuranceCategoryAndMakeCall(val) {
      this.updateInsuranceCategory(val);
      this.makeCall()
    },
    extraFieldAdd(field) {
      this.additionalFieldData = {...this.additionalFieldData, ...field};
    },
    bookShipment(val) {
      this.$emit('bookShipment', {
        droppoint_no: val.droppoint_no,
        product: val.product, //this.selectedProduct was used, cannot be used here cause its not up to date, it is updated on select product event and some actions are done after that event
        notification: val,
        services: this.selectedServices,
        additionalFieldData: this.additionalFieldData,
        pickup_driver_note: val.pickup_driver_note,
        pickup_time_from: val.pickup_time_from,
        pickup_time_until: val.pickup_time_until,
        pickup_action: val.pickup_action,
        pickup_ship_date: val.pickup_ship_date
      });
    },
    validateDataForProducts(paramLog) {
      // console.log('VALIDATE: ', paramLog)
      if(this.dataForProducts.subaccountId == '') return false;
      if(this.dataForProducts.shippingDate == '') return false;
      if(!this.isInfoValid('from')) return false;
      if(!this.isInfoValid('to')) return false;
      if(this.dataForProducts.activeGoodsType == '') return false;
      if(!this.isColliValid(this.dataForProducts.colli)) return false;
      return true;
    },
    isInfoValid(type) {
      // if(this.dataForProducts[`${type}Data`].company == '' || this.dataForProducts[`${type}Data`].company == undefined || this.dataForProducts[`${type}Data`].company == null) return false;
      if(this.dataForProducts[`${type}Address`].post_code == '' || this.dataForProducts[`${type}Address`].post_code == undefined || this.dataForProducts[`${type}Address`].post_code == null) return false;
      if(this.dataForProducts[`${type}Address`].country_id == '' || this.dataForProducts[`${type}Address`].country_id == undefined || this.dataForProducts[`${type}Address`].country_id == null) return false;
      if(this.dataForProducts[`${type}Address`].address_1 == '' || this.dataForProducts[`${type}Address`].address_1 == undefined || this.dataForProducts[`${type}Address`].address_1 == null) return false;
      if(this.dataForProducts[`${type}Address`].city == '' || this.dataForProducts[`${type}Address`].city == undefined || this.dataForProducts[`${type}Address`].city == null) return false;
      let refCheck;
      if(type == 'from') refCheck = this.dataForProducts.isSenderReferenceRequired;
      if(type == 'to') refCheck = this.dataForProducts.isReceiverReferenceRequired;
      if(refCheck){
        if(this.dataForProducts[`${type}Data`].reference == '' || this.dataForProducts[`${type}Data`].reference == null) return false;
      }
      return true;
    },
    isColliValid(colliArray) {
      let result = true;
      if (colliArray.length < 1) {
        result = false;
      }

      colliArray.forEach((colli, index) => {
        const groupIndex = parseInt(colli.group.replace(/^\D+/g, ''));
        if (this.showColliContent[groupIndex]) {
          colli.content_json.forEach((cnt) => {
            if (cnt.unit_weight == '') result = false;
          })
        } else {
          if (colli.weight == '' ||
            colli.weight == '0' ||
            colli.weight == 0 ||
            colli.weight == undefined ||
            colli.weight == 'undefined' ||
            colli.weight == null) {
            result = false;
          }
        }

        if(colli.isTemperature == true){
          if(colli.min_temp == '' || colli.max_temp == ''){
            result = false;
          }
        };
        if(colli.isDgToggleActive == true){
          colli.services_json.forEach((service) => {
            if (service.fee_wrapper_id === null || service.fee_wrapper_id === ""
              || service.dangerous_goods_id == null || service.dangerous_goods_id === ""
              || service.brutto_weight == null || service.brutto_weight === ""
            ) {
              result = false;
            }
          });

        };
        if(colli.hasOwnProperty('cubic_meter')){
          if(
            (colli.cubic_meter == '' && colli.load_meter == '') &&
            (colli.length == '' || colli.width == '' || colli.height == '')){
            result = false;
          }
        } else {
          if(colli.length == '' || colli.width == '' || colli.height == '') {
            result = false;
          }
        }
      });

      return result;
    },
    makeCall: _.debounce(function(event) {
      this.reloadingProducts = true;
      this.update++;
      this.$store.dispatch('GET_AVAILABLE_PRODUCTS', {
        sender_id: this.shipperAsSenderID,
        subaccount_id: this.dataForProducts.subaccountId,
        ship_date: this.dataForProducts.shippingDate,
        sender: {
          company_name: this.dataForProducts.fromData.company,
          post_code: this.dataForProducts.fromAddress.post_code,
          country_code: this.getCountry(this.dataForProducts.fromAddress.country_id, 'alpha2'),
          address_1: this.dataForProducts.fromAddress.address_1,
          address_2: this.dataForProducts.fromAddress.address_2,
          city: this.dataForProducts.fromAddress.city,
        },
        customer: {
          //If is ship to business resident and there is no company name, rate request should pass, so we need to use fake company for BE logic
          company_name: (!this.shipToPrivateResident && !this.dataForProducts.toData.company) ? 'Fake company' : this.dataForProducts.toData.company,
          post_code: this.dataForProducts.toAddress.post_code,
          country_code: this.getCountry(this.dataForProducts.toAddress.country_id, 'alpha2'),
          address_1: this.dataForProducts.toAddress.address_1,
          address_2: this.dataForProducts.toAddress.address_2,
          city: this.dataForProducts.toAddress.city,
        },
        should_send_customs_file: this.dataForProducts.showCustoms ? '1' : '0',
        goods_type: this.dataForProducts.activeGoodsType,
        is_droppoint: this.isDroppoint,
        colli: this.dataForProducts.colli,
        goods_incoterm: this.dataForProducts.customsData.goods_incoterm,
        duty_paid_by: this.dataForProducts.customsData.duty_paid_by,
        duty_payer_account_no: this.dataForProducts.customsData.duty_payer_account_no,
        freight_paid_by: this.dataForProducts.customsData.freight_paid_by,
        freight_payer_account_no: this.dataForProducts.customsData.freight_payer_account_no,
        freight_payer_id: this.dataForProducts.customsData.freight_payer_id,
        fee_wrapper_ids_json: [...this.selectedServices, ...this.incotermServiceIds],
        active_services_json: this.additionalFieldData,
        goods_types: this.dataForProducts.activeGoodsTypes
      }).then((res) => {
        //this.enabledProductsByServices = [];
        //this.enabledServicesByProduct = [];
        this.servicesList = res ? res.services : [];
        this.productsList = res ? res.products : [];
        this.servicesAllowed = res ? res.service_ids : [];
        this.productsAllowed = res ? res.product_ids : [];
        this.showPrices      = res ? res.show_prices : false;
        this.showProductType = res ? res.show_product_type.toString() : '1';

        // Check if selected product is in products list
        let isProductInList = false;
        this.productsList.forEach((product) => {
          if(product.shipping_product_id == this.selectedProductId) isProductInList = true;
        })
        if(this.selectedProductId != 0 && this.productsList != null && isProductInList){
          this.selectedProduct = this.productsList.filter((product) => Number(product.shipping_product_id) == this.selectedProductId)[0];
          this.enabledServicesByProduct = this.selectedProduct.allowed_services;
        }
        this.reloadingProducts = false;
        if(this.apiFeeIds && this.apiFeeIds.length > 0 && !this.externalApiLoaded){
          this.externalApiLoaded = true;
          // CHECK FOR ENABLED PRODUCTS BY SELECTED SERVICES THAT ARE PASSED FORM API
          this.productsList.forEach((product) => {
            product.allowed_services.forEach((id) => {
              if(this.isProductAllowed(product.shipping_product_id)) {
                this.enabledProductsByServices.push(parseInt(product.shipping_product_id));
              }
            });
          });
        }
      });
    }, 500),
    selectProduct(val) {
      this.update++;
      if((this.selectedProduct != null) && (val.product.id == this.selectedProduct.id) ) {
        this.selectedProduct = null;
        this.enabledServicesByProduct = [];
        return;
      }
      this.enabledServicesByProduct = val.allowedServices;
      this.selectedProduct = val.product;
      this.insuranceCategory = null; //Reset insurance category on product select
    },
    isProductAllowed(productId) {
      var result = true;
      this.servicesList.forEach((serviceGroup) => {
        Object.keys(serviceGroup.services).forEach((key) => {
          if(this.selectedServices.includes(serviceGroup.services[key].id)){
            if(!serviceGroup.services[key].allowed_products.includes(productId)){
              result = false;
              return;
            }
          }
        })
        return;
      })
      return result;
    },
    servicesUpdate(val) {
      console.log('SERVICES - servicesUpdate')
      this.reloadingProducts = true;
      this.selectedServices = val.services;
      this.additionalFieldData = val.additionalFieldData;
      // FILTER
      this.enabledProductsByServices = [];
      if(this.selectedServices.length > 0){
        this.productsList.forEach((product) => {
          product.allowed_services.forEach((id) => {
            if(this.isProductAllowed(product.shipping_product_id)) {
              this.enabledProductsByServices.push(parseInt(product.shipping_product_id));
            }
          });
        });
      } else {
        this.enabledProductsByServices = this.productsAllowed.map((id) => parseInt(id));
      }
      this.makeCall();
    },
  },
  watch: {
    additionalServicesFieldsData: {
      handler() {
        if(this.additionalServicesFieldsData != null) {
          this.additionalFieldData = this.additionalServicesFieldsData;
        }
      },
      deep: true,
    },
    apiActiveServices: {
      handler(newVal, oldVal){
        if(this.apiActiveServices != null){
          Object.keys(this.apiActiveServices).forEach((key, index) => {
            this.additionalFieldData[this.apiActiveServices[key].fee_wrapper_id] = {
              currency: this.apiActiveServices[key].currency,
              name: this.apiActiveServices[key].name,
              service_category: this.apiActiveServices[key].service_category,
              value: this.apiActiveServices[key].value,
            }
          });
        }
      },
      deep: true
    },
    apiFeeIds: {
      handler(newVal, oldVal){
        if(this.apiFeeIds && this.apiFeeIds.length > 0){
          this.selectedServices = this.apiFeeIds;
        }
      },
      deep: true
    },
    additionalFieldData: {
      handler(newVal, oldVal){
        if(this.servicesList == null) return false;
        this.servicesList.forEach((serviceGroup) => {
          Object.keys(serviceGroup.services).forEach((key) => {
            if(this.selectedServices.indexOf() > -1){
              result = true;
            }
            let serviceCategoryId = serviceGroup.id;
            let ServiceId = serviceGroup.services[key].id;
            if(this.additionalFieldData.hasOwnProperty(ServiceId)){
              if(_.isEqual(oldVal[ServiceId], newVal[ServiceId]) == false && this.validateDataForProducts('additionalFieldData')){

                //If service category is insurance and insurance category received from child component exist, update additionalFieldData
                if (serviceCategoryId == 9 && this.insuranceCategory) {
                  this.updateInsuranceCategory(this.insuranceCategory)
                }

                this.makeCall();
              }
            }
          })
        })
      },
      deep: true
    },
    observableObject: {
      handler(newVal, oldVal){
        if(_.isEqual(oldVal, newVal) == false && this.validateDataForProducts('observableObject')){
          this.makeCall();
        }
      },
      deep: true
    },
    fromReferenceListener: {
      handler(newVal, oldVal){
        if(!newVal.required) return;
        if(newVal.val == oldVal.val) return;
        if(this.validateDataForProducts('fromReferenceListener')){
          console.log('fromReferenceListener')
          this.makeCall();
        }
      },
      deep: true
    },
    toReferenceListener: {
      handler(newVal, oldVal){
        if(!newVal.required) return;
        if(newVal.val == oldVal.val) return;
        if(this.validateDataForProducts('toReferenceListener')){
          console.log('toReferenceListener')
          this.makeCall();
        }
      },
      deep: true
    },
    companyFromListener: {
      handler(newVal, oldVal){
        if(newVal == '' && oldVal == '') return;
        if(newVal.length >= 1  && oldVal.length >= 1) return;
        if(newVal == '' && oldVal.length >= 1 || newVal.length >= 1 && oldVal == ''){
          if(this.validateDataForProducts('companyFromListener')){
            this.makeCall();
          }
        }
      },
    },
    companyToListener: {
      handler(newVal, oldVal){
        if(newVal == '' && oldVal == '') return;
        if(newVal.length >= 1  && oldVal.length >= 1) return;
        if(newVal == '' && oldVal.length >= 1 || newVal.length >= 1 && oldVal == ''){
          if(this.validateDataForProducts('companyToListener')){
            this.makeCall();
          }
        }
      },
    },
    shipToPrivateResident: {
      handler(newVal, oldVal) {
        if (newVal == oldVal) return;
        if (this.validateDataForProducts('shipToPrivateResident')) {
          this.makeCall();
        }
      },
    },
    'customsData.freight_payer_id': {
      handler(){
        this.makeCall();
      },
    },
    'customsData.freight_paid_by': {
      handler(){
        this.makeCall();
      },
    },
    isDroppoint: {
      handler(newVal, oldVal){
        console.log('droppoint watcher')
        this.makeCall()
      },
      deep: true
    },
    editShipmentDroppointNo: {
      handler(newVal, oldVal){
        this.isDroppoint = !!newVal;
      },
      deep: true
    },
  },
}
</script>
